import React, { Suspense, lazy } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import './i18n'; // Import i18n configuration
import HomePage from './pages/HomePage';
import RouteSeo from './components/seo/RouteSeo';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import EmergencyCallout from './components/layout/EmergencyCallout';
import { useModal } from './contexts/ModalContext';
import ScheduleServiceModal from './components/modals/ScheduleServiceModal';

// Other routes load on demand so the homepage does not download invoices, admin, or jspdf.
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetailPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const TestimonialsPage = lazy(() => import('./pages/TestimonialsPage'));
const ContactUsPage = lazy(() => import('./pages/ContactUsPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('./pages/auth/RegisterPage'));
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'));
const CustomersPage = lazy(() => import('./pages/admin/CustomersPage'));
const AdminServiceRequestsPage = lazy(() => import('./pages/admin/AdminServiceRequestsPage'));
const ServiceRequestsManagementPage = lazy(() => import('./pages/admin/ServiceRequestsManagementPage'));
const AdminServiceRequestDetailPage = lazy(() => import('./pages/admin/AdminServiceRequestDetailPage'));
const EstimatesPage = lazy(() => import('./pages/admin/EstimatesPage'));
const QuotesPage = lazy(() => import('./pages/admin/QuotesPage'));
const InvoicesPage = lazy(() => import('./pages/admin/InvoicesPage'));
const CreateEstimatePage = lazy(() => import('./pages/admin/CreateEstimatePage'));
const ViewEstimatePage = lazy(() => import('./pages/admin/ViewEstimatePage'));
const EditEstimatePage = lazy(() => import('./pages/admin/EditEstimatePage'));
const NewQuotePage = lazy(() => import('./pages/admin/NewQuotePage'));
const ViewQuotePage = lazy(() => import('./pages/admin/ViewQuotePage'));
const EditQuotePage = lazy(() => import('./pages/admin/EditQuotePage'));
const NewInvoicePage = lazy(() => import('./pages/admin/NewInvoicePage'));
const ViewInvoicePage = lazy(() => import('./pages/admin/ViewInvoicePage'));
const EditInvoicePage = lazy(() => import('./pages/admin/EditInvoicePage'));
const ExpensesPage = lazy(() => import('./pages/admin/ExpensesPage'));
const CreateExpensePage = lazy(() => import('./pages/admin/CreateExpensePage'));
const ViewExpensePage = lazy(() => import('./pages/admin/ViewExpensePage'));
const EditExpensePage = lazy(() => import('./pages/admin/EditExpensePage'));
const TaxReportingPage = lazy(() => import('./pages/admin/TaxReportingPage'));
const InventoryPage = lazy(() => import('./pages/admin/InventoryPage'));
const InventoryFormPage = lazy(() => import('./pages/admin/InventoryFormPage'));
const InventoryDetailPage = lazy(() => import('./pages/admin/InventoryDetailPage'));
const ProfileSettingsPage = lazy(() => import('./pages/user/ProfileSettingsPage'));
const MyServiceRequestsPage = lazy(() => import('./pages/user/MyServiceRequestsPage'));
const PInvoicesPage = lazy(() => import('./pages/PInvoicesPage'));
const PejmanPage = lazy(() => import('./pages/PejmanPage'));
const CardLandingPage = lazy(() => import('./pages/CardLandingPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const SitemapPage = lazy(() => import('./pages/SitemapPage'));

// Main App component to house the modal logic
function App() {
  const { isModalOpen, closeModal, modalType } = useModal();
  const location = useLocation();
  const isPejmanApp = location.pathname === '/pejman';
  const isCardLanding = location.pathname === '/card';

  let page;
  if (isCardLanding) {
    page = (
      <Routes>
        <Route path="/card" element={<CardLandingPage />} />
      </Routes>
    );
  } else if (isPejmanApp) {
    page = (
      <div className="min-h-screen font-sans bg-slate-100 text-slate-900">
        <Routes>
          <Route path="/pejman" element={<PejmanPage />} />
        </Routes>
      </div>
    );
  } else {
    page = (
    <div className="flex flex-col min-h-screen font-sans bg-brand-background-light text-brand-text-dark">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-[80] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-brand-text-dark focus:shadow-lg"
      >
        Skip to content
      </a>
      <EmergencyCallout />
      <Header />
      <main id="main-content" tabIndex={-1} className="flex-grow py-8 overflow-y-auto">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/testimonials" element={<TestimonialsPage />} />
          <Route path="/contact" element={<ContactUsPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/admin/users" element={<UserManagementPage />} />
          <Route path="/admin/customers" element={<CustomersPage />} />
          <Route path="/admin/service-requests" element={<AdminServiceRequestsPage />} />
          <Route path="/admin/service-requests-management" element={<ServiceRequestsManagementPage />} />
          <Route path="/admin/service-requests/:id" element={<AdminServiceRequestDetailPage />} />
          <Route path="/admin/estimates" element={<EstimatesPage />} />
          <Route path="/admin/estimates/new" element={<CreateEstimatePage />} />
          <Route path="/admin/estimates/view/:estimateId" element={<ViewEstimatePage />} />
          <Route path="/admin/estimates/edit/:estimateId" element={<EditEstimatePage />} />
          <Route path="/admin/quotes" element={<QuotesPage />} />
          <Route path="/admin/quotes/new" element={<NewQuotePage />} />
          <Route path="/admin/quotes/view/:quoteId" element={<ViewQuotePage />} />
          <Route path="/admin/quotes/edit/:quoteId" element={<EditQuotePage />} />
          <Route path="/admin/invoices" element={<InvoicesPage />} />
          <Route path="/admin/invoices/new" element={<NewInvoicePage />} />
          <Route path="/admin/invoices/view/:invoiceId" element={<ViewInvoicePage />} />
          <Route path="/admin/invoices/edit/:invoiceId" element={<EditInvoicePage />} />
          <Route path="/admin/expenses" element={<ExpensesPage />} />
          <Route path="/admin/expenses/new" element={<CreateExpensePage />} />
          <Route path="/admin/expenses/view/:expenseId" element={<ViewExpensePage />} />
          <Route path="/admin/expenses/edit/:expenseId" element={<EditExpensePage />} />
          <Route path="/admin/tax-reporting" element={<TaxReportingPage />} />
          <Route path="/admin/inventory" element={<InventoryPage />} />
          <Route path="/admin/inventory/new" element={<InventoryFormPage />} />
          <Route path="/admin/inventory/:id" element={<InventoryDetailPage />} />
          <Route path="/admin/inventory/:id/edit" element={<InventoryFormPage />} />
          <Route path="/profile-settings" element={<ProfileSettingsPage />} />
          <Route path="/my-service-requests" element={<MyServiceRequestsPage />} />
          <Route path="/pinvoices" element={<PInvoicesPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          <Route path="/sitemap" element={<SitemapPage />} />
        </Routes>
      </main>
      <Footer />
      <ScheduleServiceModal 
        isOpen={isModalOpen && (modalType === 'scheduleService' || modalType === 'getEstimate')} 
        onClose={closeModal} 
      />
    </div>
    );
  }

  return (
    <>
      <RouteSeo />
      <Suspense fallback={<div className="min-h-screen bg-brand-background-light" />}>
        {page}
      </Suspense>
    </>
  );
}

export default App; 